/**
 * The email templates Lattice sends and the one content rule every email body must pass.
 *
 * `TEMPLATES` mirrors the template table in specs/technical/messaging.md. A template's kind decides
 * whether the marketing preference applies to it (MSG-042), so the kind is looked up here by id
 * and never passed by the caller who is sending.
 *
 * Email is not a private channel. It is forwarded, read on shared screens and kept by providers
 * Lattice has no agreement with, so a body carries links into the signed-in account and never a
 * lab value (MSG-044). `labValueIn` finds the shapes a lab value takes in prose: a number with a
 * lab unit ("24 ng/mL"), and a biomarker name followed by a number ("Ferritin: 18", "HbA1c 5.4%").
 * The second shape also catches a dose written after an ingredient that shares a biomarker's
 * name ("Magnesium 200 mg"), which an email must not carry either.
 */
import { BIOMARKERS } from "./biomarkers.ts";

/** Marketing emails respect the unsubscribe. Transactional and staff emails always send. */
export type MessageKind = "transactional" | "marketing" | "staff";

export interface TemplateEntry {
  readonly name: string;
  readonly kind: MessageKind;
}

export const TEMPLATES = {
  "MSG-001": { name: "Verify email", kind: "transactional" },
  "MSG-002": { name: "Sign-in link", kind: "transactional" },
  "MSG-010": { name: "Your formula is ready", kind: "transactional" },
  "MSG-011": { name: "Checkout reminders", kind: "marketing" },
  "MSG-012": { name: "Formula not approved", kind: "transactional" },
  "MSG-013": { name: "Review is taking longer", kind: "transactional" },
  "MSG-014": { name: "Order confirmed", kind: "transactional" },
  "MSG-015": { name: "Shipped, delivered, action needed", kind: "transactional" },
  "MSG-020": { name: "Monthly check-in", kind: "transactional" },
  "MSG-021": { name: "Refills running out", kind: "transactional" },
  "MSG-022": { name: "Payment failed", kind: "transactional" },
  "MSG-030": { name: "Practitioner invitation", kind: "transactional" },
  "MSG-031": { name: "Clinic link request", kind: "transactional" },
  "MSG-032": { name: "Staff invitation", kind: "transactional" },
  "MSG-033": { name: "Review queue alert", kind: "staff" },
  "MSG-034": { name: "Suspected reaction", kind: "staff" },
  "MSG-035": { name: "Practitioner formula changed", kind: "transactional" },
} as const satisfies Readonly<Record<string, TemplateEntry>>;

export type TemplateId = keyof typeof TEMPLATES;

export function isTemplateId(id: string): id is TemplateId {
  return Object.hasOwn(TEMPLATES, id);
}

const MICRO = "µ";

/*
  "%" is left out of the unit list. A bare percentage is ordinary copy ("20% off"), so percentage
  markers (HbA1c, omega-3 index, hematocrit) are caught by the name-then-number shape instead.
*/
const EXTRA_UNITS = ["nmol/L", "pmol/L", "mIU/L", "IU/L", "mEq/L", "g/L", "ng/L", "K/µL"];

const UNITS = [...new Set([...BIOMARKERS.map((b) => b.unit), ...EXTRA_UNITS])].filter(
  (unit) => unit !== "%",
);

/** The short names a biomarker goes by in prose: "Vitamin D" for "Vitamin D, 25-hydroxy". */
const ALIASES = ["HbA1c", "A1c", "HDL", "LDL", "ApoB", "CRP", "B12", "T4"];

const NAMES = [
  ...new Set([...BIOMARKERS.map((b) => b.name.split(",")[0]?.trim() ?? b.name), ...ALIASES]),
];

function escape(literal: string): string {
  return literal.replaceAll(/[.*+?^${}()|[\]\\/]/g, String.raw`\$&`);
}

/** A unit as a pattern, accepting "u" and "mc" where the micro sign is written. */
function unitPattern(unit: string): string {
  return escape(unit).replaceAll(MICRO, `(?:${MICRO}|u|mc)`);
}

/** Longest first, so "mg/dL" is tried before a shorter unit that is its prefix. */
function alternatives(items: readonly string[], toPattern: (item: string) => string): string {
  return [...items]
    .sort((a, b) => b.length - a.length)
    .map(toPattern)
    .join("|");
}

/*
  The number may not follow a letter, digit, dot, hyphen, slash or equals sign, so ids and tokens in
  a link ("u-3fL", "v=12") do not read as values.
*/
const NUMBER = String.raw`(?<![\w.\-/=])[<>]?\d+(?:[.,]\d+)?`;

const WITH_UNIT = new RegExp(
  String.raw`${NUMBER}\s*(?:${alternatives(UNITS, unitPattern)})(?![\w/])`,
  "iu",
);

/* A separator is required, so "Vitamin D3" (an ingredient name) is not a value. */
const AFTER_NAME = new RegExp(
  String.raw`(?<![\p{L}\d])(?:${alternatives(NAMES, escape)})(?:\s*[:=]\s*|\s+(?:(?:is|was|of|at)\s+)?)[<>]?\d+(?:[.,]\d+)?`,
  "iu",
);

/** What a reader sees in an HTML body: tags become spaces, so a value split across cells reads as one. */
function visibleText(html: string): string {
  return html
    .replaceAll(/<(style|script)[\s\S]*?<\/\1>/gi, " ")
    .replaceAll(/<[^>]*>/g, " ")
    .replaceAll(/&nbsp;|&#160;/gi, " ")
    .replaceAll(/&micro;|&#181;|&#xb5;/gi, MICRO);
}

/** The first lab value in some text, or null. Greek mu and the micro sign are treated alike. */
export function labValueIn(text: string): string | null {
  const normalized = text.replaceAll("μ", MICRO);

  return WITH_UNIT.exec(normalized)?.[0] ?? AFTER_NAME.exec(normalized)?.[0] ?? null;
}

export interface EmailContent {
  readonly subject: string;
  readonly text: string;
  readonly html: string;
}

/** The first lab value anywhere in an email's subject, text or HTML body, or null. */
export function labValueInEmail(content: EmailContent): string | null {
  return (
    labValueIn(content.subject) ?? labValueIn(content.text) ?? labValueIn(visibleText(content.html))
  );
}
